import { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { AlignLeft, AlignVerticalJustifyStart, Ruler } from "lucide-react";
import { DUR, EASE_OUT } from "../lib/motion";

/**
 * Renders one projection profile as an SVG area chart.
 *
 * Bars are normalised against the profile's own peak, because the absolute ON
 * pixel count depends on render density and says nothing on its own — the shape
 * is what marks lines and words.
 */
function Profile({ values, height = 46, tone = "accent", label, animate = true }) {
  const prefersReducedMotion = useReducedMotion();

  const path = useMemo(() => {
    if (!values?.length) return "";
    const peak = Math.max(...values, 1);
    const step = 100 / values.length;
    const points = values.map((value, index) => {
      const x = index * step;
      const y = 100 - (value / peak) * 100;
      return `${x.toFixed(3)},${y.toFixed(3)}`;
    });
    return `M0,100 L${points.join(" L")} L100,100 Z`;
  }, [values]);

  if (!path) return null;

  const stroke = tone === "danger" ? "var(--rose-500)" : "var(--accent)";
  const fill = tone === "danger" ? "url(#projFillDanger)" : "url(#projFillAccent)";

  return (
    <svg
      className="proj__chart"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ height }}
      role="img"
      aria-label={label}
    >
      <defs>
        <linearGradient id="projFillAccent" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(18,226,126,0.55)" />
          <stop offset="100%" stopColor="rgba(18,226,126,0.02)" />
        </linearGradient>
        <linearGradient id="projFillDanger" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,95,86,0.55)" />
          <stop offset="100%" stopColor="rgba(255,95,86,0.02)" />
        </linearGradient>
      </defs>
      <motion.path
        d={path}
        fill={fill}
        stroke={stroke}
        strokeWidth="0.6"
        vectorEffect="non-scaling-stroke"
        initial={animate && !prefersReducedMotion ? { opacity: 0, scaleY: 0 } : false}
        whileInView={{ opacity: 1, scaleY: 1 }}
        viewport={{ once: true, margin: "0px 0px -8% 0px" }}
        transition={
          prefersReducedMotion ? { duration: 0 } : { duration: DUR.reveal, ease: EASE_OUT }
        }
        style={{ transformOrigin: "50% 100%" }}
      />
    </svg>
  );
}

/**
 * The paper's multiple histogram projections, as returned by the backend.
 *
 * HPH[i] (Eq. 1) counts ON pixels per row, so each peak is a text line. VPH[j]
 * (Eq. 2) counts them per column within a line, so each peak is a word. The gap
 * widths below each line are the diagnostic the paper leans on: injected
 * characters occupy space without depositing ink, so they widen the apparent
 * word gaps while leaving the glyph count untouched (Figs. 9-13).
 */
function HistogramProjection({ projection, tone = "accent", maxLines = 3 }) {
  if (!projection) return null;

  const lines = (projection.lines || []).slice(0, maxLines);
  const widestGap = Math.max(
    0,
    ...lines.flatMap((line) => line.gap_widths || [])
  );

  return (
    <div className="proj">
      <div className="proj__block">
        <div className="proj__head">
          <span className="proj__title">
            <AlignVerticalJustifyStart size={12} aria-hidden="true" />
            HPH[i] · dark pixels per row
          </span>
          <span className="proj__meta">
            {projection.line_count} {projection.line_count === 1 ? "line" : "lines"}
          </span>
        </div>
        <Profile
          values={projection.horizontal_profile}
          height={52}
          tone={tone}
          label={`Horizontal projection with ${projection.line_count} peaks, one per text line`}
        />
        <p className="proj__caption">
          One peak per line of text, counted across all {projection.width} columns of the page.
        </p>
      </div>

      {lines.length > 0 && (
        <div className="proj__block">
          <div className="proj__head">
            <span className="proj__title">
              <AlignLeft size={12} aria-hidden="true" />
              VPH[j] · dark pixels per column, by line
            </span>
            <span className="proj__meta">
              {projection.word_count} words · {projection.glyph_count} glyphs
            </span>
          </div>

          <div className="proj__lines">
            {lines.map((line, index) => (
              <div className="proj__line" key={`${line.top}-${index}`}>
                <span className="proj__line-tag">L{index + 1}</span>
                <div className="grow">
                  <Profile
                    values={line.vertical_profile}
                    height={34}
                    tone={tone}
                    label={`Line ${index + 1} vertical projection, ${line.word_count} words`}
                  />
                  <div className="proj__line-meta">
                    <span>{line.word_count} words</span>
                    <span>{line.glyph_count} glyphs</span>
                    <span className="proj__gaps" title="Blank column runs between glyphs">
                      <Ruler size={10} aria-hidden="true" />
                      gaps{" "}
                      {(line.gap_widths || []).slice(0, 14).map((gap, gapIndex) => (
                        <b
                          key={gapIndex}
                          data-wide={gap >= line.gap_threshold}
                          className="proj__gap"
                        >
                          {gap}
                        </b>
                      ))}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="proj__caption">
            Gaps of {lines[0]?.gap_threshold ?? 0}px or more are the spaces between words, shown
            highlighted. A hidden letter still takes up room even though it never shows up, so the
            gap it sits in comes out wider
            {widestGap > 0 && <>, and the widest here is {widestGap}px</>}, while the number of
            letters stays the same.
          </p>
        </div>
      )}
    </div>
  );
}

export default HistogramProjection;
